import React from 'react';
import { escapeHtml } from './helpers';
import { getPlatformData } from './socialIcons';

export type FooterSocialIconProps = {
  icon: string;
  iconColor?: string;
  size?: number;
  iconStyle?: string;
};

/** Brand icons normalized to 24×24 viewBox (paths centered, not left-heavy). */
export const FooterSocialIcon: React.FC<FooterSocialIconProps> = ({
  icon,
  iconColor = '#FFFFFF',
  size = 16,
  iconStyle = 'filled',
}) => {
  const isOutline = iconStyle === 'outline';
  const strokeWidth = isOutline ? 1.5 : 0;
  const fillColor = isOutline ? 'none' : iconColor;
  const strokeColor = isOutline ? iconColor : 'none';

  const svgProps: React.SVGProps<SVGSVGElement> = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    xmlns: 'http://www.w3.org/2000/svg',
    fill: fillColor,
    stroke: strokeColor,
    strokeWidth,
    'aria-hidden': true,
    style: {
      width: size,
      height: size,
      display: 'block',
      flexShrink: 0,
    },
  };

  switch (icon) {
    case 'facebook':
      return (
        <svg {...svgProps}>
          {isOutline ? (
            <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
          ) : (
            <path d="M9.101 23.691v-7.98H6.127v-3.667h2.974v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.043 8.043 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374 1.036-.374 1.878v1.379h3.938l-.532 3.667h-3.406v7.98H9.101z" />
          )}
        </svg>
      );
    case 'instagram':
      if (isOutline) {
        return (
          <svg {...svgProps}>
            <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
          </svg>
        );
      }
      return (
        <svg {...svgProps}>
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
      );
    case 'twitter':
    case 'x':
      return (
        <svg {...svgProps}>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      );
    case 'linkedin':
      if (isOutline) {
        return (
          <svg {...svgProps}>
            <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
            <rect x="2" y="9" width="4" height="12" />
            <circle cx="4" cy="4" r="2" />
          </svg>
        );
      }
      return (
        <svg {...svgProps}>
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      );
    case 'youtube':
      return (
        <svg {...svgProps}>
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      );
    default:
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="10" />
        </svg>
      );
  }
};

export const renderFooterSocialIcon = (
  icon: string,
  iconColor: string = '#FFFFFF',
  size: number = 16,
  iconStyle: string = 'filled'
) => <FooterSocialIcon icon={icon} iconColor={iconColor} size={size} iconStyle={iconStyle} />;

export type FooterSocialIconsRowProps = {
  platforms: Array<{ name?: string; url?: string; icon?: string; customIconUrl?: string }>;
  boxSize: number;
  iconSize: number;
  bgColor: string;
  iconColor: string;
  borderRadius: string | number;
  iconStyle?: string;
  align?: 'left' | 'center' | 'right';
};

/**
 * Builder canvas social row — inline-block + grid (never a full-width table).
 * Email HTML uses table-based footerSocialButtonHtml instead.
 */
export const FooterSocialIconsRow: React.FC<FooterSocialIconsRowProps> = ({
  platforms,
  boxSize,
  iconSize,
  bgColor,
  iconColor,
  borderRadius,
  iconStyle = 'filled',
  align = 'center',
}) => {
  if (!platforms?.length) return null;

  const radius = typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius;
  const rowJustify =
    align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center';

  return (
    <div
      className="footer-social-icons-row"
      style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignItems: 'center',
        justifyContent: rowJustify,
        gap: 10,
        width: 'auto',
        maxWidth: '100%',
        lineHeight: 0,
        fontSize: 0,
        margin: align === 'center' ? '0 auto' : undefined,
      }}
    >
      {platforms.map((platform, index) => {
        const isCustom = platform.icon === 'custom' && platform.customIconUrl;
        return (
          <a
            key={index}
            href={platform.url || '#'}
            onClick={(e) => e.preventDefault()}
            title={platform.name}
            className="footer-social-icon-box"
            style={
              {
                '--footer-social-box': `${boxSize}px`,
                '--footer-social-icon': `${iconSize}px`,
                backgroundColor: bgColor,
                borderRadius: radius,
              } as React.CSSProperties
            }
          >
            {isCustom ? (
              <img
                src={platform.customIconUrl}
                alt={platform.name || 'Social'}
                width={iconSize}
                height={iconSize}
                className="footer-social-icon-graphic"
                style={{ width: iconSize, height: iconSize, objectFit: 'contain' }}
              />
            ) : (
              <FooterSocialIcon
                icon={platform.icon || 'facebook'}
                iconColor={iconColor}
                size={iconSize}
                iconStyle={iconStyle}
              />
            )}
          </a>
        );
      })}
    </div>
  );
};

function sanitizeSvgColor(color: string): string {
  const c = (color || '#FFFFFF').trim();
  if (/^#[0-9A-Fa-f]{3,8}$/.test(c)) return c;
  if (/^rgba?\(\s*[\d.,\s%]+\s*\)$/i.test(c)) return c;
  return '#FFFFFF';
}

/** Raster-friendly SVG data URI for email clients that strip inline &lt;svg&gt;. */
export function footerSocialIconDataUri(icon: string, iconColor: string, size: number): string {
  const key = (icon || 'facebook').toLowerCase();
  const platform = getPlatformData(key);
  const pathD =
    platform?.path || 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z';
  const fill = sanitizeSvgColor(iconColor);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${size}" height="${size}"><path fill="${fill}" d="${pathD}"/></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * Email HTML social glyph — uses &lt;img&gt; + data URI (Gmail/Outlook remove inline SVG).
 */
export function footerSocialIconEmailHtml(
  icon: string,
  iconColor: string,
  size: number,
  alt = ''
): string {
  const src = footerSocialIconDataUri(icon, iconColor, size);
  const label = escapeHtml(alt || getPlatformData(icon)?.label || icon);
  const px = `${size}px`;
  return `<img src="${src}" alt="${label}" width="${size}" height="${size}" class="footer-social-icon-img" border="0" style="display:block;width:${px} !important;height:${px} !important;max-width:${px} !important;min-width:${px} !important;border:0;margin:0 auto;" />`;
}

/** @deprecated Use footerSocialIconEmailHtml */
export function footerSocialIconPathSvg(
  icon: string,
  iconColor: string,
  size: number,
  alt = ''
): string {
  return footerSocialIconEmailHtml(icon, iconColor, size, alt);
}
